import React, { useState } from 'react';

import { Grid, GridItem, Text } from '@chakra-ui/react';

import _ from 'lodash';

import {
  DistrictOutput,
  PricingQuoteResponseOutput,
  ClientType,
  ClientBudget,
  ClientComplexity,
  ResearchComplexity,
  UniverseType,
} from '../../../lib/pricing-tool-api';
import NumOfQuestionChooser from './NumOfQuestionChooser';
import { ProposalListSection } from './ProposalListSection';
import QuoteListSection from './QuoteListSection';
import RecruitmentCostSection from './RecruitmentCostSection';
import SampleSizeChooser from './SampleSizeChooser';
import {
  MAX_PRICING_QUOTE_PRICES,
  MAX_QUESTIONS,
  MAX_SAMPLE_SIZE,
  MIN_QUESTIONS,
  MIN_SAMPLE_SIZE,
} from './utils';


function GenerateQuoteSection({
  numberOfQuestions,
  setNumberOfQuestions,
  sampleSizes,
  setSampleSizes,
  districts,
  maxRecommendedSampleSize,
  generalMaxRecommendedSampleSize,
  demPrimaryMaxRecommendedSampleSize,
  pricingErrorMessage,
  isPricingLoading,
  pricedQuote,
  clientType,
  clientBudget,
  clientComplexity,
  researchComplexity,
  universeTypes,
}: {
  numberOfQuestions: number[];
  setNumberOfQuestions: (numberOfQuestions: number[]) => void;
  sampleSizes: number[];
  setSampleSizes: (sampleSizes: number[]) => void;
  districts: DistrictOutput[];
  maxRecommendedSampleSize: number;
  generalMaxRecommendedSampleSize: number;
  demPrimaryMaxRecommendedSampleSize: number;
  pricingErrorMessage: string;
  isPricingLoading: boolean;
  pricedQuote: PricingQuoteResponseOutput | null;
  clientType: ClientType;
  clientBudget: ClientBudget;
  clientComplexity: ClientComplexity;
  researchComplexity: ResearchComplexity;
  universeTypes: UniverseType[];
}) {
  const [numberOfQuestionsToAdd, setNumberOfQuestionsToAdd] =
    useState(MIN_QUESTIONS);
  const [sampleSizeToAdd, setSampleSizeToAdd] = useState(MIN_SAMPLE_SIZE);

  const canAddMoreQuestions =
    numberOfQuestions.length !== MAX_PRICING_QUOTE_PRICES &&
    !isPricingLoading &&
    !numberOfQuestions.includes(numberOfQuestionsToAdd) &&
    districts.length !== 0 &&
    _.inRange(numberOfQuestionsToAdd, MIN_QUESTIONS, MAX_QUESTIONS + 1);

  const canAddMoreSampleSizes =
    sampleSizes.length !== MAX_PRICING_QUOTE_PRICES &&
    !isPricingLoading &&
    !sampleSizes.includes(sampleSizeToAdd) &&
    districts.length !== 0 &&
    _.inRange(sampleSizeToAdd, MIN_SAMPLE_SIZE, MAX_SAMPLE_SIZE + 1);

  const addQuestionCount = () => {
    setNumberOfQuestions(
      [...numberOfQuestions, numberOfQuestionsToAdd].sort((a, b) => a - b),
    );
  };

  const addSampleSize = () => {
    setSampleSizes([...sampleSizes, sampleSizeToAdd].sort((a, b) => a - b));
  };

  return (
    <Grid templateColumns="repeat(2, 1fr)" gap="20px">
      <GridItem colSpan={2}>
        Maximum Recommended Sample Size — Registered Voters/General Election:
        <Text
          display="inline"
          fontWeight="semibold"
          color={
            generalMaxRecommendedSampleSize > 0
              ? 'brand.linkColor'
              : 'current'
          }
        >
          {' '}
          {generalMaxRecommendedSampleSize > 0
            ? generalMaxRecommendedSampleSize.toLocaleString('en-US')
            : '-'}
        </Text>
        {' '}| Dem Primary:
        <Text
          display="inline"
          fontWeight="semibold"
          color={
            demPrimaryMaxRecommendedSampleSize > 0
              ? 'brand.linkColor'
              : 'current'
          }
        >
          {' '}
          {demPrimaryMaxRecommendedSampleSize > 0
            ? demPrimaryMaxRecommendedSampleSize.toLocaleString('en-US')
            : '-'}
        </Text>
      </GridItem>

      <GridItem colSpan={1}>
        <SampleSizeChooser
          setSampleSizeToAdd={setSampleSizeToAdd}
          addSampleSize={addSampleSize}
          sampleSizeToAdd={sampleSizeToAdd}
          canAddMoreSampleSizes={canAddMoreSampleSizes}
          isLoading={isPricingLoading}
          sampleSizes={sampleSizes}
          setSampleSizes={setSampleSizes}
          calculatedMaxRecommendedSampleSize={maxRecommendedSampleSize}
        />
      </GridItem>

      <GridItem colSpan={1}>
        <NumOfQuestionChooser
          setNumberOfQuestionsToAdd={setNumberOfQuestionsToAdd}
          canAddMoreQuestions={canAddMoreQuestions}
          addQuestionCount={addQuestionCount}
          isLoading={isPricingLoading}
          numberOfQuestions={numberOfQuestions}
          setNumberOfQuestions={setNumberOfQuestions}
          numberOfQuestionsToAdd={numberOfQuestionsToAdd}
        />
      </GridItem>

      <GridItem>
        <RecruitmentCostSection
          calculatedMaxRecommendedSampleSize={maxRecommendedSampleSize}
          quotes={pricedQuote ? pricedQuote.prices : []}
          numberOfQuestions={numberOfQuestions}
          sampleSizes={sampleSizes}
        />
      </GridItem>

      <GridItem>
        <QuoteListSection
          calculatedMaxRecommendedSampleSize={maxRecommendedSampleSize}
          quotes={pricedQuote ? pricedQuote.prices : []}
          numberOfQuestions={numberOfQuestions}
          sampleSizes={sampleSizes}
        />
      </GridItem>

      <GridItem colSpan={2}>
        <ProposalListSection
          calculatedMaxRecommendedSampleSize={maxRecommendedSampleSize}
          quotes={pricedQuote ? pricedQuote.prices : []}
          clientType={clientType}
          clientBudget={clientBudget}
                  clientComplexity={clientComplexity}
        researchComplexity={researchComplexity}
          universeTypes={universeTypes}
          numberOfQuestions={numberOfQuestions}
          sampleSizes={sampleSizes}
        />
      </GridItem>

      <GridItem visibility={pricingErrorMessage ? 'visible' : 'hidden'}>
        <Text size="sm" color="emphasisTextColor">
          {pricingErrorMessage}
        </Text>
      </GridItem>
    </Grid>
  );
}

export default GenerateQuoteSection;
